import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { getNotifications, getNotificationsStats, setAllNotificationsAsRead, setNotificationReadAt } from '../api/notifications.api'
import { NotificationModel, NotificationType, PlatformType } from '../models/notifications.model'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useEffect, useMemo, useState, useCallback, useContext } from 'react'
import { useMediaQuery, useTheme } from '@mui/material'
import { NotificationContextProps, NotificationPayload, NotificationsFilterWithoutSetter } from '../types'
import { isUndefined } from 'lodash'
import { parseActiveFilter } from '../notifications.functions'
import { isUnauthorizedAuthFetchError } from 'src/Libs/api/utils'
import { isBefore } from '@e3dc-react/shell/Libs/date'
import { useTypedSelector } from 'src/State/RootReducer'
import { AuthContext } from '@e3dc-react/shell/Libs/authentication'

const NOTIFICATION_LIMIT = 10

/*
 * The NotificationsHook provides notifications and functions for handeling notifications
 * notifications are set in this hook and are used by getting notifications from this hook
 * after calling loadNotifications the notifications state can be used.
 * eg.:
 * const {notifications, loadNotifications} = useNotifications()
 *
 * useEffect(() => loadNotifications().catch(error => console.error(error)),[])
 */
const useNotifications = (): NotificationContextProps => {
  const authFetch = useAuthFetchWithResponse()
  const { authToken } = useContext(AuthContext)
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const platform = downMd ? PlatformType.Mobile : PlatformType.Desktop
  const user = useTypedSelector(state => state.user.userData)

  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const [notifications, setNotifications] = useState<NotificationModel[]>([])
  const [limitedNotifications, setLimitedNotifications] = useState<NotificationModel[]>([])
  const [unreadNotifications, setUnreadNotifications] = useState<NotificationModel[]>([])
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState<number>(0)
  const [limit, setLimit] = useState<number>(NOTIFICATION_LIMIT)
  const [offset, setOffset] = useState<number>(0)
  const [notificationsCount, setNotificationsCount] = useState<number>(0)
  const startAtSort = '-startAt,-createdAt'

  const getMandatoryNotifications = useCallback((notifications: NotificationModel[]) => {
    return notifications.filter(
      notification =>
        (notification.type === NotificationType.Mandatory || notification.type === NotificationType.Survey) &&
        notification.readAt === null &&
        (notification.stopAt ? isBefore(new Date(), new Date(notification.stopAt)) : true)
    )
  }, [])

  const mandatoryNotifications = useMemo<NotificationModel[]>(() => {
    return getMandatoryNotifications(notifications)
  }, [getMandatoryNotifications, notifications])

  useEffect(() => {
    const unreadNotifications = notifications.filter(notification => !notification.readAt)
    setUnreadNotifications(unreadNotifications)
  }, [notifications])

  const loadNotifications = useCallback(async (): Promise<NotificationModel[]> => {
    const payload: NotificationPayload = { platform, sort: startAtSort }
    const apiResult = await getNotifications(payload, authFetch)

    if (apiResult.hasErrors) {
      if (isUnauthorizedAuthFetchError(apiResult.error)) console.error('unauthorized loading of notifications')
      else enqueueErrorStack(t('notifications.load.failed'))
    }

    if (apiResult.value) {
      setNotifications(apiResult.value)
      return apiResult.value
    }

    return []
  }, [authFetch, enqueueErrorStack, platform, t])

  const loadLimitedNotifications = useCallback(
    async (activeFilters?: NotificationsFilterWithoutSetter): Promise<void> => {
      const payload: NotificationPayload = { platform, limit, offset, sort: startAtSort }
      if (!isUndefined(activeFilters)) {
        const parsedFilters = parseActiveFilter(activeFilters)
        payload.filter = parsedFilters.filter
      }
      const apiResult = await getNotifications(payload, authFetch)

      if (apiResult.hasErrors) {
        if (isUnauthorizedAuthFetchError(apiResult.error)) console.error('unauthorized loading of limited notifications')
        else enqueueErrorStack(t('notifications.load.failed'))
      }

      if (apiResult.value) setLimitedNotifications(apiResult.value)
    },
    [authFetch, enqueueErrorStack, limit, offset, platform, t]
  )

  const loadNotificationStats = useCallback(
    async (activeFilters?: NotificationsFilterWithoutSetter): Promise<void> => {
      if (authToken === null) return
      const payload: NotificationPayload = { platform }
      if (!isUndefined(activeFilters)) {
        const parsedFilters = parseActiveFilter(activeFilters)
        payload.filter = parsedFilters.filter
      }

      const statsApiResult = await getNotificationsStats(payload, authFetch)

      if (statsApiResult.hasErrors) {
        if (isUnauthorizedAuthFetchError(statsApiResult.error)) console.error('unauthorized loading of notification stats')
        else enqueueErrorStack(t('notifications.statLoad.failed'))
      }

      if (statsApiResult.value) setNotificationsCount(statsApiResult.value.count)
    },
    [authFetch, enqueueErrorStack, platform, t, authToken]
  )

  const loadUnreadNotificationsCount = useCallback(async (): Promise<void> => {
    if (authToken === null) return

    const payload = { platform, filter: `readAt=ISNULL` }
    const statsApiResult = await getNotificationsStats(payload, authFetch)

    if (statsApiResult.hasErrors) {
      if (isUnauthorizedAuthFetchError(statsApiResult.error)) console.error('unauthorized loading of unread notification stats')
      else enqueueErrorStack(t('notifications.statLoad.failed'))
    }
    if (statsApiResult.value) setUnreadNotificationsCount(statsApiResult.value.count)
  }, [authFetch, enqueueErrorStack, platform, t, authToken])

  const markNotificationAsRead = useCallback(
    async (notificationId: number): Promise<void> => {
      const { hasErrors } = await setNotificationReadAt(notificationId, authFetch)
      if (hasErrors) {
        enqueueErrorStack(t('notification.notification-read.failed'))
      } else {
        enqueueSuccessStack(t('notification.notification-read.success'))
        await loadNotifications()
        await loadUnreadNotificationsCount()
      }
    },
    [authFetch, enqueueErrorStack, enqueueSuccessStack, loadNotifications, loadUnreadNotificationsCount, t]
  )

  const markAllNotificationsAsRead = useCallback(
    async (activeFilters?: NotificationsFilterWithoutSetter): Promise<void> => {
      const { hasErrors } = await setAllNotificationsAsRead(user!.ID, authFetch)

      if (hasErrors) {
        enqueueErrorStack(t('notification.notification-read-all.failed'))
      } else {
        enqueueSuccessStack(t('notification.notification-read-all.success'))
        await loadLimitedNotifications(activeFilters)
        await loadNotifications()
        await loadUnreadNotificationsCount()
      }
    },
    [authFetch, enqueueErrorStack, enqueueSuccessStack, loadLimitedNotifications, loadNotifications, loadUnreadNotificationsCount, t, user]
  )

  return {
    loadNotifications,
    loadLimitedNotifications,
    loadNotificationStats,
    loadUnreadNotificationsCount,
    markNotificationAsRead,
    notifications,
    limitedNotifications,
    mandatoryNotifications,
    offset,
    setOffset,
    limit,
    setLimit,
    notificationsCount,
    unreadNotificationsCount,
    unreadNotifications,
    markAllNotificationsAsRead,
    getMandatoryNotifications,
  }
}

export default useNotifications
